import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { gruposApi } from '../../api/grupos'
import { Empresa, EmpresaCreate } from '../../types/api'
import { sortByLocale } from '../../utils/array'

// Schema de validação será criado dinamicamente com traduções
const createEmpresaSchema = (t: (key: string) => string) => z.object({
  nome: z.string().min(1, t('companies.name') + ' é obrigatório').max(120, t('companies.name') + ' deve ter no máximo 120 caracteres'),
  grupo_id: z.number().min(1, t('companies.group') + ' é obrigatório'),
  cnpj: z.string().max(18, t('companies.cnpj') + ' deve ter no máximo 18 caracteres').optional().nullable(),
  banco_dados: z.string().max(32, t('companies.database') + ' deve ter no máximo 32 caracteres').optional().nullable(),
  ip: z.string().max(96, t('companies.ip') + ' deve ter no máximo 96 caracteres').optional().nullable(),
  login_banco: z.string().max(16, 'Login do banco de dados deve ter no máximo 16 caracteres').optional().nullable(),
  senha_banco: z.string().max(16, 'Senha do banco de dados deve ter no máximo 16 caracteres').optional().nullable(),
  versao_banco_java: z.string().max(50, 'Versão do banco Java deve ter no máximo 50 caracteres').optional().nullable(),
  descricao: z.string().optional().nullable(),
  status: z.enum(['A', 'I']).optional().nullable(),
})

type EmpresaFormData = z.infer<ReturnType<typeof createEmpresaSchema>>

interface EmpresaFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: EmpresaCreate) => Promise<void>
  empresa?: Empresa | null
  loading?: boolean
}

const EmpresaForm: React.FC<EmpresaFormProps> = ({
  open,
  onClose,
  onSubmit,
  empresa,
  loading = false,
}) => {
  const { t } = useTranslation()
  const empresaSchema = React.useMemo(() => createEmpresaSchema(t), [t])
  const [usarOverrides, setUsarOverrides] = useState<boolean>(false)
  
  // Buscar grupos para o select
  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos', 'ativos'],
    queryFn: () => gruposApi.list({ status: 'A' }),
    enabled: open, // Só buscar quando o dialog estiver aberto
  })
  const safeGrupos = React.useMemo(() => {
    const lista = Array.isArray(grupos) ? grupos : []
    return sortByLocale(lista, (g) => g.nome ?? '')
  }, [grupos])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<EmpresaFormData>({
    resolver: zodResolver(empresaSchema),
    defaultValues: {
      nome: empresa?.nome || '',
      grupo_id: empresa?.grupo_id || 0,
      cnpj: empresa?.cnpj || '',
      banco_dados: empresa?.banco_dados || '',
      ip: empresa?.ip || '',
      login_banco: empresa?.login_banco || '',
      senha_banco: empresa?.senha_banco || '',
      versao_banco_java: empresa?.versao_banco_java || '',
      descricao: empresa?.descricao || '',
      status: (empresa?.status as 'A' | 'I') || 'A',
    },
  })

  const grupoId = watch('grupo_id')

  // Resetar formulário quando empresa mudar ou dialog abrir/fechar
  useEffect(() => {
    if (open) {
      reset({
        nome: empresa?.nome || '',
        grupo_id: empresa?.grupo_id || 0,
        cnpj: empresa?.cnpj || '',
        banco_dados: empresa?.banco_dados || '',
        ip: empresa?.ip || '',
        login_banco: empresa?.login_banco || '',
        senha_banco: empresa?.senha_banco || '',
        versao_banco_java: empresa?.versao_banco_java || '',
        descricao: empresa?.descricao || '',
        status: (empresa?.status as 'A' | 'I') || 'A',
      })
      // Carregar preferência de overrides do tenancy ao editar
      if (empresa?.id) {
        // Importar sob demanda para evitar ciclo
        import('../../api/empresas').then(({ empresasApi }) => {
          empresasApi.getTema(empresa.id).then((tema) => {
            const flag = Boolean(((tema || {}).features || {}).usar_overrides_telas)
            setUsarOverrides(flag)
          }).catch(() => setUsarOverrides(false))
        })
      } else {
        setUsarOverrides(false)
      }
    }
  }, [open, empresa, reset])

  const handleFormSubmit = async (data: EmpresaFormData) => {
    try {
      // Converter null para undefined para compatibilidade com EmpresaCreate
      const submitData: EmpresaCreate = {
        nome: data.nome,
        grupo_id: data.grupo_id,
        cnpj: data.cnpj || undefined,
        banco_dados: data.banco_dados || undefined,
        ip: data.ip || undefined,
        login_banco: data.login_banco || undefined,
        senha_banco: data.senha_banco || undefined,
        versao_banco_java: data.versao_banco_java || undefined,
        descricao: data.descricao || undefined,
        status: data.status || undefined,
        usar_overrides_telas: usarOverrides,
      }
      await onSubmit(submitData)
      reset()
      onClose()
    } catch (error) {
      // Erro será tratado pelo componente pai
      console.error('Erro ao salvar empresa:', error)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {empresa ? t('companies.edit') : t('companies.new')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              {...register('nome')}
              label={t('companies.name')}
              fullWidth
              margin="normal"
              error={!!errors.nome}
              helperText={errors.nome?.message}
              disabled={loading}
              autoFocus
            />

            <FormControl fullWidth margin="normal" error={!!errors.grupo_id}>
              <InputLabel>{t('companies.group')}</InputLabel>
              <Select
                value={grupoId || ''}
                label={t('companies.group')}
                disabled={loading}
                {...register('grupo_id', { valueAsNumber: true })}
                onChange={(e) => setValue('grupo_id', Number(e.target.value))}
              >
                {safeGrupos.map((grupo) => (
                  <MenuItem key={grupo.id} value={grupo.id}>
                    {grupo.nome}
                  </MenuItem>
                ))}
              </Select>
              {errors.grupo_id && (
                <Box component="span" sx={{ fontSize: '0.75rem', color: 'error.main', mt: 0.5, ml: 1.75 }}>
                  {errors.grupo_id.message}
                </Box>
              )}
            </FormControl>

            <TextField
              {...register('cnpj')}
              label={t('companies.cnpj')}
              fullWidth
              margin="normal"
              error={!!errors.cnpj}
              helperText={errors.cnpj?.message}
              disabled={loading}
            />

            <TextField
              {...register('banco_dados')}
              label={t('companies.database')}
              fullWidth
              margin="normal"
              error={!!errors.banco_dados}
              helperText={errors.banco_dados?.message}
              disabled={loading}
            />

            <TextField
              {...register('ip')}
              label={t('companies.ip')}
              fullWidth
              margin="normal"
              error={!!errors.ip}
              helperText={errors.ip?.message}
              disabled={loading}
            />

            <TextField
              {...register('login_banco')}
              label="Usuário Banco de Dados"
              fullWidth
              margin="normal"
              error={!!errors.login_banco}
              helperText={errors.login_banco?.message}
              disabled={loading}
            />

            <TextField
              {...register('senha_banco')}
              label="Senha Banco de Dados"
              fullWidth
              margin="normal"
              type="password"
              error={!!errors.senha_banco}
              helperText={errors.senha_banco?.message}
              disabled={loading}
            />

            <TextField
              {...register('versao_banco_java')}
              label="Versão Banco Java (Legado)"
              fullWidth
              margin="normal"
              error={!!errors.versao_banco_java}
              helperText={errors.versao_banco_java?.message || 'Versão do banco de dados no sistema Thor/Java (ex: 1.5.0)'}
              disabled={loading}
            />

            <TextField
              {...register('descricao')}
              label={t('companies.description') || 'Descrição'}
              fullWidth
              margin="normal"
              multiline
              rows={4}
              error={!!errors.descricao}
              helperText={errors.descricao?.message}
              disabled={loading}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>{t('companies.status')}</InputLabel>
              <Select
                value={watch('status') || 'A'}
                label={t('companies.status')}
                disabled={loading}
                {...register('status')}
              >
                <MenuItem value="A">{t('common.active')}</MenuItem>
                <MenuItem value="I">{t('common.inactive')}</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={usarOverrides}
                  onChange={(_, v) => setUsarOverrides(v)}
                  disabled={loading}
                />
              }
              label="Usar overrides de telas (menu por empresa)"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default EmpresaForm
